import type { CreditProduct, DebtConsolidationProduct, LoanProduct, MeResponse } from '@/api';
import { isMeResponseSE } from '@/api/me/utils';
import { type ProductName, ProductNames } from '@/constants/ProductNames';
import { assertCondition, assertUnreachable } from '@/utils/typescript';

export const selectUsername = (me: MeResponse) => me.givenName ?? me.firstName;

export const selectFullName = (me: MeResponse) => `${me.firstName ?? me.givenName} ${me.lastName}`;

export const selectIsFirstDebtTransfer = (me: MeResponse) =>
  !('debtConsolidation' in me.activatedProducts && me.activatedProducts.debtConsolidation);

export function createActivatedProductSelector(
  productName: Extract<ProductName, 'credit'>,
): (me: MeResponse) => CreditProduct;

export function createActivatedProductSelector(
  productName: Extract<ProductName, 'debtConsolidation'>,
): (me: MeResponse) => DebtConsolidationProduct;

export function createActivatedProductSelector(
  productName: Extract<ProductName, 'loan'>,
): (me: MeResponse) => LoanProduct;

export function createActivatedProductSelector(
  productName: ProductName,
): (me: MeResponse) => CreditProduct | DebtConsolidationProduct | LoanProduct;

export function createActivatedProductSelector(productName: ProductName) {
  return (me: MeResponse) => {
    let product;

    switch (productName) {
      case ProductNames.CREDIT:
        assertCondition(ProductNames.CREDIT in me.activatedProducts);
        product = me.activatedProducts.credit;
        break;
      case ProductNames.DEBT_CONSOLIDATION:
        assertCondition(ProductNames.DEBT_CONSOLIDATION in me.activatedProducts);
        product = me.activatedProducts.debtConsolidation;
        break;
      case ProductNames.LOAN: {
        assertCondition(ProductNames.LOAN in me.activatedProducts);
        product = me.activatedProducts.loan;
        break;
      }
      default:
        assertUnreachable(productName);
    }

    assertCondition(!!product);

    return product;
  };
}

export const createAnnualInterestSelector = (productName: ProductName) => (me: MeResponse) => {
  const activatedProduct = createActivatedProductSelector(productName)(me);
  return activatedProduct.annualInterest;
};

export const selectIsLoanUser = (me: MeResponse) => {
  assertCondition(isMeResponseSE(me), 'Me response is not SE');

  return !!me.activatedProducts.loan;
};

export const selectSEUserPersonalDetails = (me: MeResponse) => {
  assertCondition(isMeResponseSE(me), 'selectSEUserPersonalDetails: Me response is not SE');

  return {
    email: me.email,
    nationalNumber: me.nationalNumber,
    mobile: me.mobile,
  };
};

export const selectHasLoanIdProtectProduct = (me: MeResponse) => {
  assertCondition(isMeResponseSE(me), 'selectHasLoanIdProtectProduct: Me response is not SE');
  return me.activatedProducts.loan?.productVariant === 'LOAN_ID_PROTECT';
};

export const selectHasLoanPartnerProduct = (me: MeResponse) => {
  assertCondition(isMeResponseSE(me), 'selectHasLoanPartnerProduct: Me response is not SE');
  return me.activatedProducts.loan?.productVariant === 'LOAN_PARTNER';
};

const NORDISKA_BANKGIRO = '5379‐6454';
const FAIRLO_BANKGIRO = '5211‐9419';

export const selectBankgiro = (me: MeResponse) => {
  if (selectHasLoanIdProtectProduct(me) || selectHasLoanPartnerProduct(me)) {
    return FAIRLO_BANKGIRO;
  }

  return NORDISKA_BANKGIRO;
};

export const selectDirectDebitMissingFee = (productName: ProductName) => (me: MeResponse) =>
  createActivatedProductSelector(productName)(me).fees.directDebitMissingFee?.amount;
